import React, { useState, useEffect, useRef } from "react";
import * as Tone from "tone";
import {
  Play,
  Pause,
  Plus,
  Trash2,
  Settings,
  Music,
  Zap,
  Volume2,
} from "lucide-react";

const WaveExplorer = () => {
  const [waves, setWaves] = useState([
    {
      id: 1,
      frequency: 440,
      amplitude: 0.5,
      color: "#3b82f6",
      enabled: true,
      showWave: false,
      waveform: "sine",
    },
  ]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayRange, setDisplayRange] = useState({
    start: 0,
    duration: 0.01,
  }); // 10ms
  const [sampleRate, setSampleRate] = useState(44100);
  const [showCombined, setShowCombined] = useState(true);

  const canvasRef = useRef(null);
  const synthsRef = useRef({});
  const animationRef = useRef(null);

  // 音色选项 - 包含白噪音
  const waveformOptions = [
    { value: "sine", label: "纯音", color: "#3b82f6" },
    { value: "piano", label: "钢琴", color: "#8b5cf6" },
    { value: "clarinet", label: "单簧管", color: "#ec4899" },
    { value: "whitenoise", label: "白噪音", color: "#6b7280" },
  ];

  // 预设颜色
  const presetColors = [
    "#3b82f6",
    "#ef4444",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#6366f1",
    "#14b8a6",
    "#f97316",
    "#84cc16",
    "#a855f7",
    "#06b6d4",
    "#eab308",
    "#f43f5e",
    "#22c55e",
  ];

  // 生成钢琴谐波预设 - 加上包络衰减，完全匹配原代码
  const generatePianoHarmonics = (baseFreq = 440) => {
    // 参考原代码中钢琴的计算公式，包括包络衰减
    const harmonics = [
      { freq: baseFreq, amp: 1.0, label: "基频(1.0)+包络" },
      { freq: baseFreq * 2, amp: 0.6, label: "2次谐波(0.6)+包络" },
      { freq: baseFreq * 3, amp: 0.4, label: "3次谐波(0.4)+包络" },
      { freq: baseFreq * 4, amp: 0.3, label: "4次谐波(0.3)+包络" },
      { freq: baseFreq * 5, amp: 0.2, label: "5次谐波(0.2)+包络" },
    ];

    const newWaves = harmonics.map((harmonic, index) => {
      const newId = Math.max(0, ...waves.map((w) => w.id)) + index + 1;
      return {
        id: newId,
        frequency: harmonic.freq,
        amplitude: harmonic.amp * 0.3, // 整体振幅调低一些，避免过载
        color: presetColors[index % presetColors.length],
        enabled: true,
        showWave: true, // 默认显示各个谐波
        waveform: "sine",
        isHarmonic: true,
        harmonicLabel: harmonic.label,
        useEnvelope: "piano", // 标记使用钢琴包络
      };
    });

    setWaves(newWaves);
  };

  // 生成单簧管谐波预设 - 加上包络衰减，完全匹配原代码
  const generateClarinetHarmonics = (baseFreq = 440) => {
    // 参考原代码中单簧管的计算公式，包括包络衰减
    const harmonics = [
      { freq: baseFreq, amp: 1.0, label: "基频(1.0)+包络" },
      { freq: baseFreq * 3, amp: 0.6, label: "3次谐波(0.6)+包络" },
      { freq: baseFreq * 5, amp: 0.4, label: "5次谐波(0.4)+包络" },
      { freq: baseFreq * 7, amp: 0.3, label: "7次谐波(0.3)+包络" },
      { freq: baseFreq * 2, amp: 0.2, label: "2次谐波(0.2)+包络" },
      { freq: baseFreq * 4, amp: 0.1, label: "4次谐波(0.1)+包络" },
    ];

    const newWaves = harmonics.map((harmonic, index) => {
      const newId = Math.max(0, ...waves.map((w) => w.id)) + index + 1;
      return {
        id: newId,
        frequency: harmonic.freq,
        amplitude: harmonic.amp * 0.3, // 整体振幅调低一些，避免过载
        color: presetColors[index % presetColors.length],
        enabled: true,
        showWave: true,
        waveform: "sine",
        isHarmonic: true,
        harmonicLabel: harmonic.label,
        useEnvelope: "clarinet", // 标记使用单簧管包络
      };
    });

    setWaves(newWaves);
  };

  // 生成传统白噪音预设 - 正弦波叠加+衰减包络
  const generateTraditionalWhiteNoise = () => {
    const noiseComponents = [];
    const numComponents = 60; // 60个频率分量

    for (let i = 0; i < numComponents; i++) {
      // 在20Hz到8000Hz范围内分布频率
      const randomFreq = Math.floor(Math.random() * 7980) + 20;
      // 较小的振幅
      const randomAmp = 0.03 + Math.random() * 0.05; // 0.03-0.08之间
      // 随机相位
      const randomPhase = Math.random() * 2 * Math.PI;

      noiseComponents.push({
        freq: randomFreq,
        amp: randomAmp,
        phase: randomPhase,
        label: `噪音${i + 1}(${randomFreq}Hz)`,
      });
    }

    const newWaves = noiseComponents.map((component, index) => {
      const newId = Math.max(0, ...waves.map((w) => w.id)) + index + 1;
      return {
        id: newId,
        frequency: component.freq,
        amplitude: component.amp,
        color: presetColors[index % presetColors.length],
        enabled: true,
        showWave: false, // 分量太多，默认不显示单独波形
        waveform: "sine",
        isNoise: true,
        noiseLabel: component.label,
        randomPhase: component.phase,
        useRandomPhase: true,
        useEnvelope: "noise", // 使用噪音衰减包络
      };
    });

    setWaves(newWaves);
  };

  // 生成真实白噪音预设 - 1秒持续时间
  const generateRealWhiteNoise = () => {
    // 创建一个真正的白噪音波形，持续1秒
    const newWave = {
      id: Math.max(0, ...waves.map((w) => w.id)) + 1,
      frequency: 0, // 白噪音没有特定频率
      amplitude: 0.3,
      color: "#6b7280",
      enabled: true,
      showWave: true,
      waveform: "whitenoise",
      isNoise: true,
      noiseLabel: "真实白噪音(1秒)",
      duration: 1.0, // 持续1秒
    };

    setWaves([newWave]);
  };

  // 清除所有波形，重置为单个纯音
  const resetToSingleTone = () => {
    setWaves([
      {
        id: 1,
        frequency: 440,
        amplitude: 0.5,
        color: "#3b82f6",
        enabled: true,
        showWave: false,
        waveform: "sine",
      },
    ]);
  };

  // 创建合成器根据音色类型 - 支持1秒白噪音
  const createSynth = (wave) => {
    let synth;

    // 传统白噪音的特殊处理
    if (wave.isNoise && wave.useEnvelope === "noise") {
      // 为传统白噪音创建带衰减包络的合成器
      synth = new Tone.Synth({
        oscillator: { type: "sine" },
        envelope: {
          attack: 0.01,
          decay: 0.8,
          sustain: 0.4,
          release: 1.0,
        },
      }).toDestination();
    }
    // 如果是谐波分析生成的波形，使用与原音色相同类型的合成器
    else if (wave.isHarmonic && wave.useEnvelope) {
      // 谐波分析使用带ADSR的Synth，确保与原音色一致
      switch (wave.useEnvelope) {
        case "piano":
          synth = new Tone.Synth({
            oscillator: { type: "sine" },
            envelope: {
              attack: 0.005,
              decay: 0.3,
              sustain: 0.3,
              release: 1.2,
            },
          }).toDestination();
          break;
        case "clarinet":
          synth = new Tone.Synth({
            oscillator: { type: "sine" }, // 谐波分析用sine，但保持相同包络
            envelope: {
              attack: 0.02,
              decay: 0.3,
              sustain: 0.6,
              release: 0.4,
            },
          }).toDestination();
          break;
        default:
          synth = new Tone.Oscillator(wave.frequency, "sine").toDestination();
          break;
      }
    } else {
      // 直接选择的音色或普通正弦波
      switch (wave.waveform) {
        case "piano":
          synth = new Tone.Synth({
            oscillator: { type: "sine" },
            envelope: {
              attack: 0.005,
              decay: 0.3,
              sustain: 0.3,
              release: 1.2,
            },
          }).toDestination();
          break;
        case "clarinet":
          synth = new Tone.Synth({
            oscillator: { type: "square" },
            envelope: {
              attack: 0.02,
              decay: 0.3,
              sustain: 0.6,
              release: 0.4,
            },
          }).toDestination();
          break;
        case "whitenoise":
          synth = new Tone.Noise("white").toDestination();
          break;
        default: // sine
          synth = new Tone.Oscillator(wave.frequency, "sine").toDestination();
          break;
      }
    }

    if (synth.volume) {
      synth.volume.value = Tone.gainToDb(
        wave.amplitude * (wave.enabled ? 1 : 0)
      );
    }
    if (synth.frequency && wave.waveform !== "whitenoise") {
      synth.frequency.value = wave.frequency;
    }

    return synth;
  };

  // 初始化和管理音频合成器
  useEffect(() => {
    // 停止播放以便重新初始化
    if (isPlaying) {
      Object.values(synthsRef.current).forEach((synth) => {
        if (synth.state === "started") {
          try {
            if (synth.triggerRelease) {
              synth.triggerRelease();
            } else {
              synth.stop();
            }
          } catch (e) {
            console.log("Error stopping synth:", e);
          }
        }
      });
    }

    // 清理所有现有合成器
    Object.values(synthsRef.current).forEach((synth) => {
      try {
        synth.dispose();
      } catch (e) {
        console.log("Error disposing synth:", e);
      }
    });
    synthsRef.current = {};

    // 为每个波形创建新的合成器
    waves.forEach((wave) => {
      const synth = createSynth(wave);
      synthsRef.current[wave.id] = synth;
    });

    // 如果之前在播放，重新开始播放
    if (isPlaying) {
      setTimeout(() => {
        waves.forEach((wave) => {
          const synth = synthsRef.current[wave.id];
          if (synth && wave.enabled) {
            try {
              if (wave.waveform === "whitenoise") {
                // 白噪音：如果有duration属性，则播放指定时间后自动停止
                synth.start();
                if (wave.duration) {
                  setTimeout(() => {
                    try {
                      synth.stop();
                    } catch (e) {
                      console.log("Error stopping timed white noise:", e);
                    }
                  }, wave.duration * 1000);
                }
              } else if (wave.isNoise && wave.useEnvelope === "noise") {
                // 传统白噪音使用triggerAttack
                if (synth.triggerAttack) {
                  synth.triggerAttack(wave.frequency);
                }
              } else if (synth.triggerAttack) {
                // 带包络的合成器使用triggerAttack
                synth.triggerAttack(wave.frequency);
              } else if (synth.start) {
                // 普通振荡器使用start
                synth.start();
              }
            } catch (e) {
              console.log("Error starting synth:", e);
            }
          }
        });
      }, 100);
    }

    return () => {
      Object.values(synthsRef.current).forEach((synth) => {
        try {
          synth.dispose();
        } catch (e) {
          console.log("Error disposing synth:", e);
        }
      });
    };
  }, [
    waves
      .map(
        (w) =>
          `${w.id}-${w.frequency}-${w.amplitude}-${w.enabled}-${w.waveform}`
      )
      .join(","),
  ]);

  // 生成波形数据（根据音色类型）- 包含白噪音支持
  const generateWaveformSample = (wave, t) => {
    const f = wave.frequency;
    const A = wave.amplitude;

    // 包络函数 - 模拟乐器的自然衰减
    const getEnvelope = (t, envelopeType) => {
      switch (envelopeType) {
        case "piano":
          return Math.exp(-t * 2.5); // 快速衰减
        case "clarinet":
          return Math.max(0.4, Math.exp(-t * 0.6)); // 中等衰减
        case "noise":
          return Math.exp(-t * 1.5); // 噪音衰减
        default:
          return 1; // 正弦波无衰减
      }
    };

    // 根据wave的useEnvelope属性决定使用哪种包络
    const envelopeType = wave.useEnvelope || wave.waveform;
    const envelope = getEnvelope(t, envelopeType);

    switch (wave.waveform) {
      case "whitenoise":
        // 白噪音：每个采样点都是随机值，有持续时间限制
        if (wave.duration && t > wave.duration) {
          return 0; // 超过持续时间则返回0
        }
        return A * (Math.random() * 2 - 1); // -1到1之间的随机值

      case "sine":
        // 如果是普通的随机相位正弦波
        if (wave.useRandomPhase && wave.randomPhase !== undefined) {
          return (
            A * Math.sin(2 * Math.PI * f * t + wave.randomPhase) * envelope
          );
        }
        return A * Math.sin(2 * Math.PI * f * t) * envelope;

      case "piano":
        // 钢琴：基频 + 逐渐减弱的谐波 + 快速衰减包络
        const piano_fundamental = Math.sin(2 * Math.PI * f * t);
        const piano_2nd = 0.6 * Math.sin(2 * Math.PI * f * 2 * t);
        const piano_3rd = 0.4 * Math.sin(2 * Math.PI * f * 3 * t);
        const piano_4th = 0.3 * Math.sin(2 * Math.PI * f * 4 * t);
        const piano_5th = 0.2 * Math.sin(2 * Math.PI * f * 5 * t);
        return (
          A *
          (piano_fundamental + piano_2nd + piano_3rd + piano_4th + piano_5th) *
          envelope
        );

      case "clarinet":
        // 单簧管：奇次谐波强，偶次谐波弱
        const clarinet_fundamental = Math.sin(2 * Math.PI * f * t);
        const clarinet_3rd = 0.6 * Math.sin(2 * Math.PI * f * 3 * t);
        const clarinet_5th = 0.4 * Math.sin(2 * Math.PI * f * 5 * t);
        const clarinet_7th = 0.3 * Math.sin(2 * Math.PI * f * 7 * t);
        const clarinet_2nd = 0.2 * Math.sin(2 * Math.PI * f * 2 * t); // 偶次谐波较弱
        const clarinet_4th = 0.1 * Math.sin(2 * Math.PI * f * 4 * t);
        return (
          A *
          (clarinet_fundamental +
            clarinet_3rd +
            clarinet_5th +
            clarinet_7th +
            clarinet_2nd +
            clarinet_4th) *
          envelope
        );

      default:
        return A * Math.sin(2 * Math.PI * f * t) * envelope;
    }
  };

  // 设置高分辨率Canvas
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // 确保canvas有合理的最小尺寸
    const canvasWidth = Math.max(rect.width, 400);
    const canvasHeight = Math.max(rect.height, 400);

    // 设置内部分辨率
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;

    // 缩放context以匹配设备像素比
    ctx.scale(dpr, dpr);

    // 设置CSS尺寸
    canvas.style.width = canvasWidth + "px";
    canvas.style.height = canvasHeight + "px";
  };

  // 生成波形数据
  const generateWaveData = () => {
    const numSamples = Math.floor(displayRange.duration * sampleRate);
    const data = new Array(numSamples).fill(0);

    waves.forEach((wave) => {
      if (!wave.enabled) return;

      for (let i = 0; i < numSamples; i++) {
        const t = displayRange.start + i / sampleRate;
        const sample = generateWaveformSample(wave, t);
        data[i] += sample;
      }
    });

    return data;
  };

  // 绘制波形
  const drawWave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(rect.width, 400);
    const height = Math.max(rect.height, 400);

    // 清空画布
    ctx.clearRect(0, 0, width, height);

    // 绘制网格
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 0.5;

    // 水平网格线
    for (let i = 0; i <= 10; i++) {
      const y = (height / 10) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // 垂直网格线
    for (let i = 0; i <= 20; i++) {
      const x = (width / 20) * i;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    // 绘制中心线
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();

    const numSamples = Math.floor(displayRange.duration * sampleRate);
    const enabledWaves = waves.filter((w) => w.enabled);

    // 绘制各个单独波形（如果选择显示）
    waves.forEach((wave) => {
      if (!wave.enabled || !wave.showWave) return;

      ctx.strokeStyle = wave.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();

      for (let i = 0; i < numSamples; i++) {
        const t = displayRange.start + i / sampleRate;
        const sample = generateWaveformSample(wave, t);
        const x = (i / numSamples) * width;
        const y = height / 2 - (sample * height) / 4;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    });

    // 绘制叠加波形（如果有多个启用的波形且选择显示叠加）
    if (enabledWaves.length > 0 && showCombined) {
      const combinedData = generateWaveData();
      ctx.strokeStyle =
        enabledWaves.length > 1 ? "#dc2626" : enabledWaves[0].color;
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = 1;
      ctx.beginPath();

      combinedData.forEach((sample, i) => {
        const x = (i / combinedData.length) * width;
        const y = height / 2 - (sample * height) / 4;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
    }
  };

  // 设置canvas并开始动画循环
  useEffect(() => {
    // 确保Canvas初始化
    const initCanvas = () => {
      setupCanvas();
      drawWave();
    };

    // 延迟初始化以确保DOM渲染完成
    setTimeout(initCanvas, 100);

    const animate = () => {
      drawWave();
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (isPlaying) {
      animate();
    } else {
      drawWave();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, waves, displayRange, sampleRate, showCombined]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        setupCanvas();
        drawWave();
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 确保组件挂载后立即绘制
  useEffect(() => {
    const timer = setTimeout(() => {
      setupCanvas();
      drawWave();
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // 播放/停止音频
  const togglePlayback = async () => {
    if (Tone.context.state === "suspended") {
      await Tone.start();
    }

    if (isPlaying) {
      Object.values(synthsRef.current).forEach((synth) => {
        try {
          if (synth.triggerRelease) {
            synth.triggerRelease();
          } else if (synth.state === "started") {
            synth.stop();
          }
        } catch (e) {
          console.log("Error stopping synth:", e);
        }
      });
      setIsPlaying(false);
    } else {
      waves.forEach((wave) => {
        const synth = synthsRef.current[wave.id];
        if (synth && wave.enabled) {
          try {
            if (wave.waveform === "whitenoise") {
              // 白噪音：如果有duration属性，则播放指定时间后自动停止
              synth.start();
              if (wave.duration) {
                setTimeout(() => {
                  try {
                    synth.stop();
                  } catch (e) {
                    console.log("Error stopping timed white noise:", e);
                  }
                }, wave.duration * 1000);
              }
            } else if (wave.isNoise && wave.useEnvelope === "noise") {
              // 传统白噪音使用triggerAttack
              if (synth.triggerAttack) {
                synth.triggerAttack(wave.frequency);
              }
            } else if (synth.triggerAttack) {
              // 带包络的合成器使用triggerAttack
              synth.triggerAttack(wave.frequency);
            } else if (synth.start) {
              // 普通振荡器使用start
              synth.start();
            }
          } catch (e) {
            console.log("Error starting synth:", e);
          }
        }
      });
      setIsPlaying(true);
    }
  };

  // 添加新波形
  const addWave = () => {
    const newId = Math.max(...waves.map((w) => w.id)) + 1;
    const colors = [
      "#3b82f6",
      "#ef4444",
      "#10b981",
      "#f59e0b",
      "#8b5cf6",
      "#ec4899",
      "#6366f1",
    ];
    const newWave = {
      id: newId,
      frequency: 440,
      amplitude: 0.5,
      color: colors[waves.length % colors.length],
      enabled: true,
      showWave: false,
      waveform: "sine",
    };
    setWaves([...waves, newWave]);
  };

  // 删除波形
  const removeWave = (id) => {
    if (waves.length > 1) {
      setWaves(waves.filter((w) => w.id !== id));
    }
  };

  // 更新波形参数
  const updateWave = (id, field, value) => {
    setWaves(
      waves.map((wave) => (wave.id === id ? { ...wave, [field]: value } : wave))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          声波探究工具-by陈硕老师
        </h1>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* 左侧控制面板 */}
          <div className="xl:col-span-1 space-y-4">
            {/* 快速预设 */}
            <div className="bg-white rounded-lg shadow-md p-3">
              <h2 className="text-sm font-semibold mb-3 flex items-center">
                <Zap className="mr-1" size={16} />
                快速预设
              </h2>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => generatePianoHarmonics(440)}
                  className="flex items-center justify-center px-1.5 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded text-xs font-medium transition-colors"
                >
                  <Music className="mr-0.5" size={10} />
                  钢琴音色合成
                </button>
                <button
                  onClick={() => generateClarinetHarmonics(440)}
                  className="flex items-center justify-center px-1.5 py-1.5 bg-pink-500 hover:bg-pink-600 text-white rounded text-xs font-medium transition-colors"
                >
                  <Music className="mr-0.5" size={10} />
                  单簧管音色合成
                </button>
                <button
                  onClick={generateTraditionalWhiteNoise}
                  className="flex items-center justify-center px-1.5 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs font-medium transition-colors"
                >
                  <Volume2 className="mr-0.5" size={10} />
                  叠加噪音
                </button>
                <button
                  onClick={generateRealWhiteNoise}
                  className="flex items-center justify-center px-1.5 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors"
                >
                  <Volume2 className="mr-0.5" size={10} />
                  真实白噪音
                </button>
                <button
                  onClick={resetToSingleTone}
                  className="col-span-2 flex items-center justify-center px-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition-colors"
                >
                  重置纯音
                </button>
              </div>
            </div>

            {/* 显示设置 */}
            <div className="bg-white rounded-lg shadow-md p-3">
              <h2 className="text-sm font-semibold mb-3 flex items-center">
                <Settings className="mr-1" size={16} />
                显示设置
              </h2>
              <div className="space-y-2">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showCombined}
                      onChange={(e) => setShowCombined(e.target.checked)}
                      className="mr-1.5"
                    />
                    <span className="text-xs font-medium">显示叠加波形</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      时间范围
                    </label>
                    <select
                      value={displayRange.duration}
                      onChange={(e) =>
                        setDisplayRange({
                          ...displayRange,
                          duration: parseFloat(e.target.value),
                        })
                      }
                      className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={0.001}>1ms</option>
                      <option value={0.005}>5ms</option>
                      <option value={0.01}>10ms</option>
                      <option value={0.02}>20ms</option>
                      <option value={0.05}>50ms</option>
                      <option value={0.1}>100ms</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      起始时间(s)
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      max="1"
                      value={displayRange.start}
                      onChange={(e) =>
                        setDisplayRange({
                          ...displayRange,
                          start: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-1.5 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 添加波形 */}
            <button
              onClick={addWave}
              className="w-full flex items-center justify-center px-2 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium transition-colors"
            >
              <Plus className="mr-1" size={12} />
              添加声波
            </button>
          </div>

          {/* 主内容区域：声波参数 + 波形图 */}
          <div className="xl:col-span-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 波形参数控制 */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">声波参数</h2>
                <div className="max-h-96 overflow-y-auto pr-2 space-y-4">
                  {waves.map((wave) => (
                    <div
                      key={wave.id}
                      className="bg-white rounded-lg shadow-md p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div
                            className="w-4 h-4 rounded-full mr-3"
                            style={{ backgroundColor: wave.color }}
                          ></div>
                          <span className="font-medium">
                            {wave.harmonicLabel
                              ? wave.harmonicLabel
                              : wave.noiseLabel
                              ? wave.noiseLabel
                              : `声波 ${wave.id}`}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={wave.enabled}
                              onChange={(e) =>
                                updateWave(wave.id, "enabled", e.target.checked)
                              }
                              className="mr-1"
                            />
                            <span className="text-sm">启用</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={wave.showWave}
                              onChange={(e) =>
                                updateWave(
                                  wave.id,
                                  "showWave",
                                  e.target.checked
                                )
                              }
                              className="mr-1"
                            />
                            <span className="text-sm">显示</span>
                          </label>
                          {waves.length > 1 && (
                            <button
                              onClick={() => removeWave(wave.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            音色:
                          </label>
                          <select
                            value={wave.waveform}
                            onChange={(e) =>
                              updateWave(wave.id, "waveform", e.target.value)
                            }
                            className="w-38 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {waveformOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <label className="text-sm font-medium text-gray-700">
                              频率:
                            </label>
                            <div className="relative">
                              <input
                                type="number"
                                min="20"
                                max="20000"
                                value={wave.frequency}
                                onChange={(e) =>
                                  updateWave(
                                    wave.id,
                                    "frequency",
                                    parseInt(e.target.value) || 440
                                  )
                                }
                                className="w-24 px-2 py-1 pr-8 border border-gray-300 rounded text-sm"
                              />
                              <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                                Hz
                              </span>
                            </div>
                          </div>
                          <input
                            type="range"
                            min="20"
                            max="2000"
                            step="1"
                            value={wave.frequency}
                            onChange={(e) =>
                              updateWave(
                                wave.id,
                                "frequency",
                                parseInt(e.target.value)
                              )
                            }
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            振幅: {(wave.amplitude * 100).toFixed(0)}%
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={wave.amplitude}
                            onChange={(e) =>
                              updateWave(
                                wave.id,
                                "amplitude",
                                parseFloat(e.target.value)
                              )
                            }
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 波形显示 */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-md p-4 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">波形图</h2>
                    {/* 播放控制 */}
                    <button
                      onClick={togglePlayback}
                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                        isPlaying
                          ? "bg-red-500 hover:bg-red-600 text-white"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                      }`}
                    >
                      {isPlaying ? (
                        <Pause className="mr-1" size={16} />
                      ) : (
                        <Play className="mr-1" size={16} />
                      )}
                      {isPlaying ? "停止" : "播放"}
                    </button>
                  </div>
                  <div className="relative h-full">
                    <canvas
                      ref={canvasRef}
                      className="border border-gray-300 rounded-lg w-full bg-white"
                      style={{
                        width: "100%",
                        height: "400px",
                        minHeight: "400px",
                        maxWidth: "100%",
                        display: "block",
                      }}
                    />
                    <div className="mt-3 text-sm text-gray-600">
                      <div className="flex flex-wrap gap-3">
                        {waves
                          .filter((w) => w.enabled && w.showWave)
                          .map((wave) => (
                            <div key={wave.id} className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-1"
                                style={{ backgroundColor: wave.color }}
                              ></div>
                              <span>
                                {wave.frequency}Hz (
                                {
                                  waveformOptions
                                    .find((opt) => opt.value === wave.waveform)
                                    ?.label.split(" ")[0]
                                }
                                )
                              </span>
                            </div>
                          ))}
                        {waves.filter((w) => w.enabled).length > 0 &&
                          showCombined && (
                            <div className="flex items-center">
                              <div
                                className={`w-3 h-3 rounded-full mr-1 ${
                                  waves.filter((w) => w.enabled).length > 1
                                    ? "bg-red-600"
                                    : ""
                                }`}
                                style={
                                  waves.filter((w) => w.enabled).length === 1
                                    ? {
                                        backgroundColor: waves.find(
                                          (w) => w.enabled
                                        ).color,
                                      }
                                    : {}
                                }
                              ></div>
                              <span>
                                {waves.filter((w) => w.enabled).length > 1
                                  ? "叠加结果"
                                  : `${
                                      waves.find((w) => w.enabled).frequency
                                    }Hz`}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 说明文档 */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">使用说明</h3>
          <div className="text-blue-800 space-y-2">
            <p>
              • <strong>快速预设：</strong>
              一键生成钢琴/单簧管的谐波分析，对比单独选择的音色和谐波叠加的区别
            </p>
            <p>
              • <strong>钢琴音色合成：</strong>
              自动生成基频+2、3、4、5次谐波，每个都是纯正弦波
            </p>
            <p>
              • <strong>单簧管谐波分析：</strong>
              自动生成奇次谐波强、偶次谐波弱的组合
            </p>
            <p>
              • <strong>叠加噪音：</strong>
              使用60个不同频率+随机相位的正弦波叠加，加上噪音衰减包络，实际上只能看到杂乱的波形，和真实白噪音有明显区别
            </p>
            <p>
              • <strong>真实白噪音：</strong>
              使用真正的白噪音生成器，持续1秒，每个采样点都是随机值
            </p>
            <p>
              • <strong>教学对比：</strong>
              先用预设看谐波分析，再用单独音色选择，听觉和视觉对比效果
            </p>
            <p>
              • <strong>启用/禁用：</strong>控制声波是否参与音频播放和波形叠加
            </p>
            <p>
              • <strong>显示控制：</strong>
              勾选"显示"可查看单独的波形，推荐在谐波分析时全部显示
            </p>
          </div>
          <div className="mt-4 bg-blue-100 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">
              一些复杂的原理补充：
            </h4>
            <div className="text-blue-800 text-sm space-y-1">
              <p>
                • <strong>音色的本质：</strong>
                相同基频的不同乐器声音不同，是因为谐波结构+包络衰减不同
              </p>
              <p>
                • <strong>叠加原理：</strong>
                复杂音色 = 基频正弦波 + 各次谐波正弦波 + 包络衰减
              </p>
              <p>
                • <strong>包络衰减：</strong>
                钢琴有快速衰减，单簧管有中等衰减，影响音色特征
              </p>
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded">
              <p className="text-blue-900 text-sm font-medium">🎯 建议流程：</p>
              <p className="text-blue-800 text-sm">
                1. 先用"钢琴音色"播放，观察复杂波形 → 2.
                再用"钢琴音色合成"，看到5个带ADSR包络的正弦波 → 3.
                对比发现：叠加的红色波形与钢琴音色波形完全一致！→ 4.
                学生理解：复杂音色来自简单波形叠加
              </p>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default WaveExplorer;
